import type { Lead } from "@/types/lead";
import type { LeadTransferCandidate } from "@/lib/lead/transferLeadOwner";
import { originLocationKey } from "@/lib/lead/locationScope";

export type LeadsManagerListFilters = {
  locationId: string | "all";
  ownerUserId: string | "all";
};

export function shouldShowLeadsManagerFilters(args: {
  viewOrgWide: boolean;
  selectableLocationCount: number;
}): boolean {
  return args.viewOrgWide || args.selectableLocationCount > 1;
}

export function shouldShowLeadsOwnerFilter(viewOrgWide: boolean): boolean {
  return viewOrgWide;
}

export function applyLeadsManagerListFilters(
  leads: Lead[],
  filters: LeadsManagerListFilters | undefined
): Lead[] {
  if (!filters) return leads;
  let out = leads;
  if (filters.locationId !== "all") {
    const lid = filters.locationId.trim();
    out = out.filter((l) => originLocationKey(l) === lid);
  }
  if (filters.ownerUserId !== "all") {
    const uid = filters.ownerUserId.trim();
    out = out.filter((l) => (l.owner_user_id || "").trim() === uid);
  }
  return out;
}

export function leadCreatedAtMs(lead: Lead): number {
  if (lead.created_at) {
    const t = Date.parse(lead.created_at);
    if (Number.isFinite(t)) return t;
  }
  const idNum = Number(lead.id);
  if (Number.isFinite(idNum) && idNum > 0) return idNum;
  return lead.lastUpdated;
}

export function displayLeadTransferMember(m: LeadTransferCandidate): string {
  return (m.display_name || m.full_name || m.email || m.user_id).trim();
}

export function buildOwnerLabelByUserId(
  members: LeadTransferCandidate[]
): Map<string, string> {
  const map = new Map<string, string>();
  for (const m of members) {
    if (m.user_id) map.set(m.user_id, displayLeadTransferMember(m));
  }
  return map;
}

export function resolveLeadOwnerLabel(
  lead: Lead,
  ownerLabelByUserId: Map<string, string>
): string | null {
  const id = (lead.owner_user_id || "").trim();
  if (!id) return null;
  return ownerLabelByUserId.get(id) ?? `${id.slice(0, 8)}…`;
}

export function resolveLeadLocationLabel(
  lead: Lead,
  locationNameById: Map<string, string>
): string | null {
  const name = (lead.origin_location_name || "").trim();
  if (name) return name;
  const id = originLocationKey(lead);
  if (!id) return null;
  return locationNameById.get(id) ?? id;
}
